import CollectorCard from '../passport/CollectorCard';
import CollectorCardSkeleton from '../passport/CollectorCardSkeleton';
import { sourceCard } from '../passport/sourceCards';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BuildingStorefrontIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/ApiService';
import { useAuth } from '../../contexts/AuthContext';
import type { Shop, ShopRequest } from '../../types/buildExpansion';
import PageShell from '../ui/PageShell';
import FeatureTip from '../onboarding/FeatureTip';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import { DirectoryBrowseLayout, FilterSection } from '../ui/DirectoryBrowseLayout';
import { ShopStatusBadge } from './ShopStatusBadge';
import { ShopRequestModal } from './ShopRequestModal';

const MAX_SERVICE_CHIPS = 3;

const ShopCard: React.FC<{ shop: Shop }> = ({ shop }) => {
  const services = shop.services || [];
  const location = [shop.city, shop.state].filter(Boolean).join(', ');
  const trust =
    shop.trust_label ||
    (shop.is_verified ? 'verified' : shop.is_community_verified ? 'community_verified' : 'listed');
  return (
    <CollectorCard
      className="shop-directory-card"
      href={`/shops/${shop.slug}`}
      card={sourceCard({
        kind: 'shop',
        id: shop.id,
        title: shop.name,
        logo: apiService.getFileUrl(shop.logo_path || '') || shop.logo_path,
        subtitle: location,
        slug: shop.slug,
        facts: {
          verification_status: trust,
          city: shop.city || null,
          specialties: services.join(' · ') || null,
        },
      })}
      fullRecordExtension={
        <>
          {(trust === 'verified' || trust === 'community_verified') && (
            <ShopStatusBadge trustLabel={trust} compact />
          )}
          {services.length > 0 && (
            <ul className="flex flex-wrap gap-1.5" aria-label="Services">
              {services.slice(0, MAX_SERVICE_CHIPS).map((service) => (
                <li key={service} className="badge">
                  {service}
                </li>
              ))}
              {services.length > MAX_SERVICE_CHIPS && (
                <li className="text-xs text-gray-400">
                  +{services.length - MAX_SERVICE_CHIPS} more
                </li>
              )}
            </ul>
          )}
        </>
      }
    />
  );
};

const requestStatusLabel = (status: ShopRequest['status']): string => {
  switch (status) {
    case 'pending':
      return 'Pending review';
    case 'needs_info':
      return 'More info requested';
    case 'approved':
      return 'Approved';
    case 'rejected':
      return 'Rejected';
    case 'merged':
      return 'Merged with existing';
    default:
      return status;
  }
};

type ShopFilters = {
  q?: string;
  city?: string;
  state?: string;
  service?: string;
  verification?: string;
  page: number;
};

function filtersFromParams(params: URLSearchParams): ShopFilters {
  return {
    q: params.get('q') || undefined,
    city: params.get('city') || undefined,
    state: params.get('state') || undefined,
    service: params.get('service') || undefined,
    verification: params.get('verification') || undefined,
    page: Math.max(1, Number(params.get('page') || 1)),
  };
}

function filtersToParams(filters: ShopFilters): URLSearchParams {
  const next = new URLSearchParams();
  if (filters.q) next.set('q', filters.q);
  if (filters.city) next.set('city', filters.city);
  if (filters.state) next.set('state', filters.state);
  if (filters.service) next.set('service', filters.service);
  if (filters.verification) next.set('verification', filters.verification);
  if (filters.page > 1) next.set('page', String(filters.page));
  return next;
}

const SHOP_REQUESTS_DISMISS_KEY = 'bilt_shops_requests_banner_dismissed';

function shopRequestsFingerprint(requests: ShopRequest[]): string {
  return requests
    .map((r) => `${r.id}:${r.status}:${r.reviewed_at || r.created_at || ''}`)
    .sort()
    .join('|');
}

function readDismissedFingerprint(): string | null {
  try {
    return window.localStorage.getItem(SHOP_REQUESTS_DISMISS_KEY);
  } catch {
    return null;
  }
}

function writeDismissedFingerprint(value: string) {
  try {
    window.localStorage.setItem(SHOP_REQUESTS_DISMISS_KEY, value);
  } catch {
    // ignore quota / private mode
  }
}

/** Public shop directory — approved shops only; request flow for new entries. */
export const ShopDirectoryPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => filtersFromParams(params), [params]);
  const [draft, setDraft] = useState<ShopFilters>(filters);
  const [searchInput, setSearchInput] = useState(filters.q || '');
  const [shops, setShops] = useState<Shop[]>([]);
  const [myRequests, setMyRequests] = useState<ShopRequest[]>([]);
  const [requestsDismissed, setRequestsDismissed] = useState(false);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requestOpen, setRequestOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setDraft(filters);
    setSearchInput(filters.q || '');
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.getShopDirectory({
        q: filters.q,
        page: filters.page,
        city: filters.city,
        state: filters.state,
        service: filters.service,
        verification: filters.verification,
      });
      setShops(result.shops);
      setTotal(result.total ?? result.shops.length);
      setTotalPages(result.total_pages ?? 1);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not load shops.');
      setShops([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!isAuthenticated) {
      setMyRequests([]);
      setRequestsDismissed(false);
      return;
    }
    void apiService
      .getMyShopRequests()
      .then((rows) => {
        setMyRequests(rows);
        const fp = shopRequestsFingerprint(rows);
        setRequestsDismissed(Boolean(fp) && readDismissedFingerprint() === fp);
      })
      .catch(() => {
        setMyRequests([]);
        setRequestsDismissed(false);
      });
  }, [isAuthenticated]);

  const dismissShopRequests = () => {
    const fp = shopRequestsFingerprint(myRequests);
    if (fp) writeDismissedFingerprint(fp);
    setRequestsDismissed(true);
  };

  const showShopRequests = isAuthenticated && myRequests.length > 0 && !requestsDismissed;

  const applyDraft = () => {
    setParams(filtersToParams({ ...draft, q: searchInput.trim() || undefined, page: 1 }), {
      replace: true,
    });
    setMobileOpen(false);
  };

  const clearAll = () => {
    setParams(new URLSearchParams(), { replace: true });
    setSearchInput('');
    setDraft({ page: 1 });
  };

  const activeCount = [filters.city, filters.state, filters.service, filters.verification].filter(
    Boolean
  ).length;
  const hasActive = activeCount > 0 || Boolean(filters.q);
  const pendingRequests = myRequests.filter(
    (r) => r.status === 'pending' || r.status === 'needs_info'
  );
  const resolvedRequests = myRequests.filter(
    (r) => r.status !== 'pending' && r.status !== 'needs_info'
  );

  const requestStatusBadgeClass = (status: ShopRequest['status']): string => {
    switch (status) {
      case 'pending':
      case 'needs_info':
        return 'bg-amber-950/50 text-amber-300 border-amber-800';
      case 'approved':
        return 'bg-green-950/50 text-green-300 border-green-800';
      case 'rejected':
        return 'bg-red-950/50 text-red-300 border-red-800';
      default:
        return 'bg-[#111] text-gray-300 border-white/[0.08]';
    }
  };

  return (
    <PageShell
      width="full"
      contentClassName="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-1 w-full"
    >
      <DirectoryBrowseLayout
        title="Shops"
        description="Tuning, fabrication, and detail shops behind documented builds. New shops are reviewed before listing."
        mobileTriggerLabel="Shops"
        sections={[{ id: 'shops-results', label: 'Results', description: 'Shops and builders' }]}
        activeFilterCount={activeCount}
        mobileOpen={mobileOpen}
        onMobileOpenChange={setMobileOpen}
        groups={[
          {
            id: 'filters',
            label: 'Filters',
            children: (
              <div className="space-y-4">
                <FilterSection id="location" label="Location">
                  <label className="block text-sm text-gray-300">
                    City
                    <input
                      className="mt-1 w-full input min-h-[40px]"
                      value={draft.city || ''}
                      onChange={(e) => setDraft({ ...draft, city: e.target.value || undefined })}
                      autoComplete="address-level2"
                    />
                  </label>
                  <label className="block text-sm text-gray-300">
                    State
                    <input
                      className="mt-1 w-full input min-h-[40px]"
                      value={draft.state || ''}
                      onChange={(e) => setDraft({ ...draft, state: e.target.value || undefined })}
                      autoComplete="address-level1"
                    />
                  </label>
                </FilterSection>
                <FilterSection id="specialty" label="Specialty">
                  <label className="block text-sm text-gray-300">
                    Service
                    <input
                      className="mt-1 w-full input min-h-[40px]"
                      value={draft.service || ''}
                      onChange={(e) => setDraft({ ...draft, service: e.target.value || undefined })}
                      placeholder="Tuning, fabrication…"
                    />
                  </label>
                </FilterSection>
                <FilterSection id="trust" label="Trust">
                  <div className="flex flex-wrap gap-2">
                    {(
                      [
                        ['verified', 'Verified'],
                        ['community_verified', 'Community'],
                        ['unverified', 'Listed'],
                      ] as const
                    ).map(([val, label]) => (
                      <button
                        key={val}
                        type="button"
                        className={
                          draft.verification === val ? 'filter-chip-active' : 'filter-chip'
                        }
                        onClick={() =>
                          setDraft({
                            ...draft,
                            verification: draft.verification === val ? undefined : val,
                          })
                        }
                        aria-pressed={draft.verification === val}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </FilterSection>
              </div>
            ),
            footer: (
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  className="flex-1"
                  onClick={() => setDraft({ page: 1 })}
                >
                  Reset
                </Button>
                <Button type="button" variant="primary" className="flex-[2]" onClick={applyDraft}>
                  Apply
                </Button>
              </div>
            ),
          },
        ]}
      >
        <div id="shops-results">
          {showShopRequests ? (
            <section
              aria-labelledby="my-requests-heading"
              className="mb-4 rounded-xl border border-white/[0.06] bg-[#0c0c0c]/50 p-4"
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <h2 id="my-requests-heading" className="heading-section text-base">
                  Your shop requests
                </h2>
                <button
                  type="button"
                  onClick={dismissShopRequests}
                  className="min-h-[40px] shrink-0 cursor-pointer px-2 text-xs text-zinc-500 hover:text-zinc-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400"
                  aria-label="Dismiss shop requests"
                >
                  Dismiss
                </button>
              </div>
              {pendingRequests.length > 0 ? (
                <>
                  <p className="text-xs text-gray-500 mb-2">Pending review</p>
                  <ul className="space-y-2 list-none mb-4">
                    {pendingRequests.map((req) => (
                      <li
                        key={req.id}
                        className="text-sm text-gray-300 flex flex-wrap items-center gap-2"
                      >
                        <span className="font-medium text-white">{req.name}</span>
                        <span
                          className={`badge border text-xs ${requestStatusBadgeClass(req.status)}`}
                        >
                          {requestStatusLabel(req.status)}
                        </span>
                        {req.review_notes ? (
                          <span className="text-gray-500 text-xs w-full">{req.review_notes}</span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
              {resolvedRequests.length > 0 ? (
                <>
                  <p className="text-xs text-gray-500 mb-2">Recent decisions</p>
                  <ul className="space-y-2 list-none">
                    {resolvedRequests.map((req) => (
                      <li
                        key={req.id}
                        className="text-sm text-gray-300 flex flex-wrap items-center gap-2"
                      >
                        <span className="font-medium text-white">{req.name}</span>
                        <span
                          className={`badge border text-xs ${requestStatusBadgeClass(req.status)}`}
                        >
                          {requestStatusLabel(req.status)}
                        </span>
                        {req.status === 'approved' && req.resulting_shop_id ? (
                          <Link
                            to={`/shops/${req.resulting_shop_id}`}
                            className="text-xs text-orange-300 hover:underline"
                          >
                            View shop
                          </Link>
                        ) : null}
                        {req.review_notes ? (
                          <span className="text-gray-500 text-xs w-full">{req.review_notes}</span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
            </section>
          ) : null}
          <FeatureTip tipId="shops_directory" className="mb-4">
            Credit the shops and contributors behind the work. Request a listing or browse verified
            portfolios.
          </FeatureTip>
          <form
            className="flex flex-col sm:flex-row gap-3 mb-4"
            onSubmit={(e) => {
              e.preventDefault();
              setParams(
                filtersToParams({ ...filters, q: searchInput.trim() || undefined, page: 1 }),
                { replace: true }
              );
            }}
            role="search"
          >
            <input
              type="search"
              className="input flex-1 min-h-[44px]"
              placeholder="Search by name, city, or specialty"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label="Search shops"
              autoComplete="off"
            />
            <Button type="submit" variant="primary">
              Search
            </Button>
          </form>

          <div className="flex flex-wrap items-center gap-2 mb-4" aria-label="Active filters">
            {filters.city ? (
              <button
                type="button"
                className="filter-chip"
                onClick={() =>
                  setParams(filtersToParams({ ...filters, city: undefined, page: 1 }), {
                    replace: true,
                  })
                }
              >
                City: {filters.city} ×
              </button>
            ) : null}
            {filters.state ? (
              <button
                type="button"
                className="filter-chip"
                onClick={() =>
                  setParams(filtersToParams({ ...filters, state: undefined, page: 1 }), {
                    replace: true,
                  })
                }
              >
                State: {filters.state} ×
              </button>
            ) : null}
            {filters.service ? (
              <button
                type="button"
                className="filter-chip"
                onClick={() =>
                  setParams(filtersToParams({ ...filters, service: undefined, page: 1 }), {
                    replace: true,
                  })
                }
              >
                Service: {filters.service} ×
              </button>
            ) : null}
            {filters.verification ? (
              <button
                type="button"
                className="filter-chip"
                onClick={() =>
                  setParams(filtersToParams({ ...filters, verification: undefined, page: 1 }), {
                    replace: true,
                  })
                }
              >
                Trust: {filters.verification.replace('_', ' ')} ×
              </button>
            ) : null}
            {hasActive ? (
              <button
                type="button"
                className="text-sm text-orange-400 hover:text-orange-300 cursor-pointer"
                onClick={clearAll}
              >
                Clear all
              </button>
            ) : null}
            <span className="text-sm text-gray-500 ml-auto" aria-live="polite">
              {total} shops
            </span>
          </div>

          <div aria-live="polite" className="sr-only">
            {loading
              ? 'Loading shops…'
              : `${shops.length} ${shops.length === 1 ? 'shop' : 'shops'} shown`}
          </div>

          {loading ? (
            <div
              className="directory-collectible-grid"
              role="status"
              aria-busy="true"
            >
              <span className="sr-only">Loading shops…</span>
              {Array.from({ length: 6 }).map((_, i) => (
                <CollectorCardSkeleton key={i} kind="shop" />
              ))}
            </div>
          ) : error ? (
            <div>
              <div role="alert" className="alert-error mb-4">
                {error}
              </div>
              <Button type="button" variant="secondary" onClick={() => void load()}>
                Try again
              </Button>
            </div>
          ) : shops.length === 0 ? (
            <EmptyState
              icon={<BuildingStorefrontIcon className="w-14 h-14" />}
              title={hasActive ? 'No shops match' : 'No shops listed yet'}
              description={
                hasActive
                  ? 'Try a different name, city, or specialty.'
                  : 'Approved shops that work on documented builds appear here.'
              }
              action={
                hasActive ? (
                  <Button type="button" variant="secondary" onClick={clearAll}>
                    Clear filters
                  </Button>
                ) : isAuthenticated ? (
                  <Button type="button" variant="primary" onClick={() => setRequestOpen(true)}>
                    Request a shop
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <>
              <ul className="directory-collectible-grid list-none">
                {shops.map((shop) => (
                  <li key={shop.id}>
                    <ShopCard shop={shop} />
                  </li>
                ))}
              </ul>
              {totalPages > 1 ? (
                <nav
                  className="flex items-center justify-center gap-3 mt-8"
                  aria-label="Shop directory pages"
                >
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={filters.page <= 1}
                    onClick={() =>
                      setParams(filtersToParams({ ...filters, page: filters.page - 1 }), {
                        replace: true,
                      })
                    }
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-gray-400" aria-current="page">
                    Page {filters.page} of {totalPages}
                  </span>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={filters.page >= totalPages}
                    onClick={() =>
                      setParams(filtersToParams({ ...filters, page: filters.page + 1 }), {
                        replace: true,
                      })
                    }
                  >
                    Next
                  </Button>
                </nav>
              ) : null}
              {isAuthenticated ? (
                <div className="mt-10 pt-6 border-t border-white/[0.06] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <p className="text-sm text-gray-400 max-w-md">
                    Know a shop that belongs on Bilt:Spec? Request a listing for review.
                  </p>
                  <Button type="button" variant="primary" onClick={() => setRequestOpen(true)}>
                    Request a shop
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </div>
      </DirectoryBrowseLayout>

      <ShopRequestModal
        isOpen={requestOpen}
        onClose={() => setRequestOpen(false)}
        initialName={filters.q}
        onSubmitted={() => {
          if (isAuthenticated) {
            void apiService.getMyShopRequests().then(setMyRequests);
          }
        }}
      />
    </PageShell>
  );
};

export default ShopDirectoryPage;
